import { useState, useCallback, useRef, useEffect } from 'react';
import type {
  ServerMessage,
  ClientMessage,
  SessionInfo,
  Participant,
} from '../types';
import type { SessionState as SharedSessionState, JournalEntry, JournalLink, GameSystemId } from '@roll1/shared';
import { sameMaterial } from '@roll1/shared';
import { getAccessToken, doRefresh } from '../lib/api';
import { uuid } from '../utils';
import { clientLog } from '../lib/client-logger';
import { LOCALE_STORAGE_KEY } from '../i18n/types';

export type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

export interface MultiplayerState {
  status: ConnectionStatus;
  session: SessionInfo | null;
  participant: Participant | null;
  participants: Participant[];
  serverState: SharedSessionState | null;
  error: string | null;
  /** Journal entries + links received with session:joined (separate from serverState) */
  initialJournal: { entries: JournalEntry[]; links: JournalLink[] } | null;
  /**
   * Increments on every session:created / session:joined (first join, real
   * reconnect, campaign switch). Marks a fresh server snapshot: consumers that
   * hydrate stores from serverState apply it once per joinSeq, not per mount.
   */
  joinSeq: number;
}

/** A player's suggestion the GM rejected (timeline:draft-rejected reaches only the author and the GMs). */
export interface TimelineRejectionNotice {
  readonly eventId: string;
  readonly title: string;
  readonly reason?: string;
}

export interface UseMultiplayerReturn extends MultiplayerState {
  /**
   * The player's rejected suggestions of this campaign, kept for the
   * connection's life: the letopis shows them whenever it is opened, until
   * dismissed. Always empty for a GM.
   */
  timelineRejections?: TimelineRejectionNotice[];
  createSession: (name: string, gameSystem?: GameSystemId) => void;
  joinSession: (code: string, name: string) => void;
  /**
   * Sends a frame. Returns its wire id — the server echoes it as
   * `correlationId` in replies that carry one (user:ui-prefs-updated) — or
   * undefined when the frame was dropped (socket not open / not joined yet).
   */
  send: (msg: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>) => string | undefined;
  disconnect: () => void;
  onMessage: (handler: (msg: ServerMessage) => void) => () => void;
  /** True while auto-rejoining a session from sessionStorage (prevents lobby flash on F5) */
  isRejoining: boolean;
}

function makeId(): string {
  return uuid();
}

function getWsUrl(): string {
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws`;
}

const MAX_RECONNECT_DELAY = 30_000;
const BASE_RECONNECT_DELAY = 1_000;
const MAX_AUTH_FAILURES = 3;
const SESSION_STORAGE_KEY = 'roll1:pending-session';

interface PendingSession { code: string; name: string; participantId?: string }

function persistSession(data: PendingSession | null) {
  try {
    if (data) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(data));
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch { /* quota exceeded or private browsing — ignore */ }
}

function restoreSession(): PendingSession | null {
  // If ?join=CODE is in the URL, skip auto-reconnect to let App.tsx handle the invite
  try {
    const params = new URLSearchParams(window.location.search);
    if (params.has('join')) return null;
  } catch { /* SSR or restricted context — ignore */ }

  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.code === 'string' && typeof parsed?.name === 'string') {
      return parsed;
    }
  } catch { /* corrupted data — ignore */ }
  return null;
}

export function useMultiplayer(): UseMultiplayerReturn {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [session, setSession] = useState<SessionInfo | null>(null);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [serverState, setServerState] = useState<SharedSessionState | null>(null);
  const [initialJournal, setInitialJournal] = useState<{ entries: JournalEntry[]; links: JournalLink[] } | null>(null);
  const [joinSeq, setJoinSeq] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [timelineRejections, setTimelineRejections] = useState<TimelineRejectionNotice[]>([]);
  // Read by the frame handler: only a player keeps rejection notices.
  const participantRef = useRef<Participant | null>(null);
  participantRef.current = participant;

  // Track whether we're auto-rejoining from sessionStorage (prevents lobby flash on F5)
  const [isRejoining, setIsRejoining] = useState(() => restoreSession() !== null);

  const wsRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef<Set<(msg: ServerMessage) => void>>(new Set());
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heartbeatTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingJoinRef = useRef<PendingSession | null>(restoreSession());
  const pendingCreateRef = useRef<string | null>(null);
  const pendingGameSystemRef = useRef<GameSystemId | undefined>(undefined);
  const intentionalCloseRef = useRef(false);
  const authPendingRef = useRef(false);
  // Tracks whether the server has confirmed our join on the current WS connection.
  // False after disconnect, true after session:joined/session:created.
  // Prevents sending commands during the reconnect race window.
  const sendReadyRef = useRef(false);
  const authFailureCountRef = useRef(0);
  // One-shot guard: re-auth retry after the server rejects a join with
  // auth_required (F5-A: anonymous join into an owned member slot).
  const authRetryRef = useRef(false);
  const lastMessageTimeRef = useRef(Date.now());

  /** Send pending join/create after auth completes (or immediately if no auth) */
  const flushPending = useCallback((ws: WebSocket) => {
    if (ws.readyState !== WebSocket.OPEN) return;

    if (pendingCreateRef.current) {
      const name = pendingCreateRef.current;
      const gameSystem = pendingGameSystemRef.current;
      pendingCreateRef.current = null;
      pendingGameSystemRef.current = undefined;
      ws.send(JSON.stringify({
        id: makeId(), type: 'session:create', timestamp: new Date().toISOString(), name,
        ...(gameSystem ? { gameSystem } : {}),
        locale: (() => { try { return localStorage.getItem(LOCALE_STORAGE_KEY) ?? undefined; } catch { return undefined; } })(),
      }));
    } else if (pendingJoinRef.current) {
      const { code, name, participantId } = pendingJoinRef.current;
      ws.send(JSON.stringify({
        id: makeId(), type: 'session:join', timestamp: new Date().toISOString(), code, name,
        ...(participantId ? { participantId } : {}),
        // the GM's UI language is the table's (Tia's calendar and time labels follow it)
        locale: (() => { try { return localStorage.getItem(LOCALE_STORAGE_KEY) ?? undefined; } catch { return undefined; } })(),
      }));
    }
  }, []);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN || wsRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setStatus('connecting');
    setError(null);

    const ws = new WebSocket(getWsUrl());
    wsRef.current = ws;

    ws.onopen = () => {
      // Do NOT set status='connected' here — the overlay must stay visible until
      // session:joined is confirmed. Otherwise commands can fire before the server
      // has registered this WS connection (race → "Session not found").

      // Start heartbeat: send ping every 25 seconds to keep connection alive
      // even when tab is in background (browsers may close idle WebSockets)
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({
            id: makeId(),
            type: 'heartbeat',
            timestamp: new Date().toISOString(),
          }));
        }
      }, 25_000);

      // Send auth message if user is logged in (first-message auth pattern).
      // The access token lives in-memory only (api.ts), so after a page reload
      // it is ALWAYS null even for a logged-in user — the refresh cookie is the
      // sole identity carrier. Always attempt doRefresh before join: skipping it
      // when the in-memory token is empty made every post-F5 join anonymous,
      // which the server (F5-A gate) rejects out of the owned member slot.
      const existingToken = getAccessToken();
      authPendingRef.current = true;
      // Race doRefresh against a 3s timeout to avoid server auth timeout (5s)
      const refreshWithTimeout = Promise.race([
        doRefresh(),
        new Promise<null>((resolve) => setTimeout(() => resolve(null), 3000)),
      ]);
      refreshWithTimeout.then((freshToken) => {
        // After async refresh, verify this WS is still current
        if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
        const token = freshToken ?? existingToken;
        if (token) {
          ws.send(JSON.stringify({
            id: makeId(),
            type: 'auth',
            timestamp: new Date().toISOString(),
            token,
          }));
          // Don't send session:join/create here — wait for auth:success/auth:error
        } else {
          // Genuinely anonymous (no refresh cookie either) — join without auth
          authPendingRef.current = false;
          flushPending(ws);
        }
      });
    };

    ws.onmessage = (event) => {
      let msg: ServerMessage;
      try {
        msg = JSON.parse(event.data as string);
      } catch {
        return;
      }

      lastMessageTimeRef.current = Date.now();
      // Handle session-level messages internally
      switch (msg.type) {
        case 'auth:success':
          authPendingRef.current = false;
          authFailureCountRef.current = 0;
          // Auth confirmed — now safe to send pending join/create
          if (wsRef.current) flushPending(wsRef.current);
          break;
        case 'auth:error':
          clientLog.warn('ws', 'auth error', { message: msg.message });
          authPendingRef.current = false;
          authFailureCountRef.current++;
          // Don't flush pending — connection is being closed by server (4001).
          // onclose handler will attempt reconnection with refreshed token.
          // After repeated failures, stop trying.
          if (authFailureCountRef.current >= MAX_AUTH_FAILURES) {
            clientLog.error('ws', 'max auth failures reached, giving up');
            intentionalCloseRef.current = true;
            setIsRejoining(false);
            setSession(null);
            setParticipant(null);
            setParticipants([]);
            setServerState(null);
            pendingJoinRef.current = null;
            persistSession(null);
          }
          break;
        case 'session:created':
        case 'session:joined':
          sendReadyRef.current = true;
          setStatus('connected');
          reconnectAttemptRef.current = 0;
          authFailureCountRef.current = 0;
          authRetryRef.current = false;
          setIsRejoining(false);
          setSession(msg.session);
          setParticipant(msg.participant);
          setParticipants(msg.participants);
          setServerState(msg.state);
          setInitialJournal({
            entries: (msg as any).journalEntries ?? [],
            links: (msg as any).journalLinks ?? [],
          });
          setJoinSeq(n => n + 1);
          // A reconnect keeps the notices; another campaign or member starts clean.
          if (pendingJoinRef.current?.code !== msg.session.code || pendingJoinRef.current?.participantId !== msg.participant.id) {
            setTimelineRejections([]);
          }
          pendingJoinRef.current = { code: msg.session.code, name: msg.participant.name, participantId: msg.participant.id };
          persistSession(pendingJoinRef.current);
          break;
        case 'session:deleted':
          setSession(null);
          setParticipant(null);
          setParticipants([]);
          setServerState(null);
          setIsRejoining(false);
          pendingJoinRef.current = null;
          persistSession(null);
          break;
        case 'command:error': {
          const cmdErr = msg as unknown as { command?: string; code?: string; error?: string };
          const isJoin = cmdErr.command === 'session:join' || cmdErr.command === 'campaign:join';
          if (isJoin && cmdErr.code === 'auth_required' && !authRetryRef.current) {
            // Server refused an anonymous rejoin into an owned slot (F5-A).
            // Retry once: refresh → auth; auth:success re-flushes the still-pending join.
            authRetryRef.current = true;
            doRefresh().then((token) => {
              if (wsRef.current !== ws || ws.readyState !== WebSocket.OPEN) return;
              if (token) {
                authPendingRef.current = true;
                ws.send(JSON.stringify({
                  id: makeId(), type: 'auth', timestamp: new Date().toISOString(), token,
                }));
              } else {
                setError(cmdErr.error ?? 'Authentication required');
                setIsRejoining(false);
              }
            });
          } else if (isJoin && cmdErr.code === 'auth_required') {
            setError(cmdErr.error ?? 'Authentication required');
            setIsRejoining(false);
          }
          break;
        }
        case 'session:error':
          setError(msg.message);
          // Clear pending session on terminal errors to stop reconnect loop
          if (msg.message === 'SESSION_NOT_FOUND' || msg.message === 'SESSION_FULL') {
            setIsRejoining(false);
            pendingJoinRef.current = null;
            persistSession(null);
            // Close WS to prevent hanging in "connecting" state
            intentionalCloseRef.current = true;
            wsRef.current?.close();
          }
          break;
        case 'participant:joined': {
          setParticipants(prev => [...prev.filter(p => p.id !== msg.participant.id), msg.participant]);
          break;
        }
        case 'participant:left': {
          setParticipants(prev =>
            prev.map(p => p.id === msg.participantId ? { ...p, online: false } : p)
          );
          break;
        }
        case 'participant:updated': {
          setParticipants(prev =>
            prev.map(p => p.id === msg.participant.id ? msg.participant : p)
          );
          // Also update the current user's participant state (e.g. assignedTokenIds)
          setParticipant(prev =>
            prev && prev.id === msg.participant.id ? msg.participant : prev
          );
          break;
        }
        // Sticker pack sync messages (Spec 058)
        case 'sticker:pack:connected': {
          const stickerPackId = (msg as any).packId as string;
          setServerState(prev => {
            if (!prev) return prev;
            const existing = prev.connectedStickerPackIds ?? [];
            if (existing.includes(stickerPackId)) return prev; // idempotent resync, no state change
            return { ...prev, connectedStickerPackIds: [...existing, stickerPackId] };
          });
          break;
        }
        case 'sticker:pack:disconnected': {
          const stickerPackId = (msg as any).packId as string;
          setServerState(prev => prev
            ? { ...prev, connectedStickerPackIds: (prev.connectedStickerPackIds ?? []).filter(id => id !== stickerPackId) }
            : prev
          );
          break;
        }
        // Homebrew sync messages (Spec 049-B)
        case 'homebrew:connected': {
          const hbPkg = (msg as any).pkg;
          setServerState(prev => prev ? { ...prev, homebrewPackages: [...(prev.homebrewPackages ?? []), hbPkg] } : prev);
          break;
        }
        case 'homebrew:disconnected': {
          const hbPkgId = (msg as any).packageId;
          setServerState(prev => prev ? { ...prev, homebrewPackages: (prev.homebrewPackages ?? []).filter(p => p.packageId !== hbPkgId) } : prev);
          break;
        }
        case 'homebrew:updated': {
          const hbUpdPkg = (msg as any).pkg;
          setServerState(prev => prev ? { ...prev, homebrewPackages: (prev.homebrewPackages ?? []).map(p => p.packageId === hbUpdPkg.packageId ? hbUpdPkg : p) } : prev);
          break;
        }
        // AI context (GM-only) — keep serverState.aiContext live so reopening
        // the settings tab shows freshly-saved values, not the join snapshot.
        case 'ai-context:updated': {
          const { aiContext: updatedCtx } = msg as any;
          setServerState(prev => prev ? { ...prev, aiContext: updatedCtx } : prev);
          break;
        }
        // Briefing lives under aiContext.sessionBriefing but arrives via its own
        // command; merge it in so the AiContextTab shows freshly-saved focus /
        // active-NPCs without a rejoin.
        case 'ai-context:briefing-updated': {
          const { briefing } = msg as any;
          setServerState(prev => {
            if (!prev) return prev;
            const nextCtx = { ...prev.aiContext, sessionBriefing: briefing } as SharedSessionState['aiContext'];
            return { ...prev, aiContext: nextCtx };
          });
          break;
        }
        // Keep serverState.navigationMode live for the CampaignTab Select
        // (the domain-level usePages hook also handles this message).
        case 'location:navigation-mode-changed': {
          const { mode: navMode } = msg as any;
          setServerState(prev => prev ? { ...prev, navigationMode: navMode } : prev);
          break;
        }
        // Timeline sync messages (Spec 053)
        case 'timeline:event-created': {
          const { event } = msg as any;
          setServerState(prev => prev ? { ...prev, timeline: [...(prev.timeline ?? []), event] } : prev);
          break;
        }
        case 'timeline:event-updated': {
          const { eventId, changes } = msg as any;
          setServerState(prev => prev ? { ...prev, timeline: (prev.timeline ?? []).map(e => e.id === eventId ? { ...e, ...changes } : e) } : prev);
          break;
        }
        case 'timeline:event-deleted': {
          const { eventId: delId } = msg as any;
          setServerState(prev => prev ? { ...prev, timeline: (prev.timeline ?? []).filter(e => e.id !== delId) } : prev);
          break;
        }
        case 'timeline:cursor-updated': {
          const { cursor } = msg as any;
          setServerState(prev => prev ? { ...prev, timelineCursor: cursor } : prev);
          break;
        }
        case 'timeline:reordered': {
          const { eventIds } = msg as any;
          setServerState(prev => {
            if (!prev) return prev;
            const tl = prev.timeline ?? [];
            const ordered = eventIds.map((id: string) => tl.find((e: any) => e.id === id)).filter(Boolean);
            const rest = tl.filter((e: any) => !eventIds.includes(e.id));
            return { ...prev, timeline: [...ordered, ...rest] };
          });
          break;
        }
        case 'timeline:draft-approved': {
          // A player never had the draft (drafts are the GMs'): the approved event is new to them.
          const { eventId: approvedId, event: approvedEvent } = msg as any;
          setServerState(prev => {
            if (!prev) return prev;
            const tl = prev.timeline ?? [];
            return { ...prev, timeline: tl.some(e => e.id === approvedId) ? tl.map(e => e.id === approvedId ? approvedEvent : e) : [...tl, approvedEvent] };
          });
          break;
        }
        case 'timeline:events-removed': {
          const removed = new Set<string>((msg as any).eventIds ?? []);
          setServerState(prev => prev ? { ...prev, timeline: (prev.timeline ?? []).filter(e => !removed.has(e.id)) } : prev);
          break;
        }
        case 'timeline:draft-rejected': {
          const { eventId: rejectedId, title: rejectedTitle, reason: rejectedReason } = msg;
          setServerState(prev => prev ? { ...prev, timeline: (prev.timeline ?? []).filter(e => e.id !== rejectedId) } : prev);
          // The author's notice outlives the letopis tab: kept here, not in the panel.
          if (participantRef.current?.role !== 'gm') {
            const notice: TimelineRejectionNotice = { eventId: rejectedId, title: rejectedTitle, ...(rejectedReason ? { reason: rejectedReason } : {}) };
            setTimelineRejections(prev => [...prev.filter(n => n.eventId !== rejectedId), notice]);
          }
          break;
        }
        // Campaign session sync messages (Spec 053)
        case 'campaign:renamed': {
          const renamed = (msg as { name: string }).name;
          setSession(prev => (prev ? { ...prev, gmName: renamed } : prev));
          break;
        }
        case 'campaign-session:created': {
          const { session: newSess } = msg as any;
          setServerState(prev => prev ? { ...prev, sessions: [...(prev.sessions ?? []), newSess] } : prev);
          break;
        }
        case 'campaign-session:updated': {
          const { sessionId: updSessId, changes: sessChanges } = msg as any;
          setServerState(prev => prev ? { ...prev, sessions: (prev.sessions ?? []).map(s => s.id === updSessId ? { ...s, ...sessChanges } : s) } : prev);
          break;
        }
        case 'campaign-session:status-changed': {
          const { sessionId: scSessId, status: scStatus } = msg as any;
          setServerState(prev => prev ? { ...prev, sessions: (prev.sessions ?? []).map(s => s.id === scSessId ? { ...s, status: scStatus } : s) } : prev);
          break;
        }
        case 'campaign-session:deleted': {
          const { sessionId: delSessId } = msg as any;
          setServerState(prev => prev ? { ...prev, sessions: (prev.sessions ?? []).filter(s => s.id !== delSessId) } : prev);
          break;
        }
        // Calendar sync messages (Spec 053)
        case 'calendar:updated': {
          const { calendar } = msg as any;
          setServerState(prev => prev ? { ...prev, calendar } : prev);
          break;
        }
        case 'calendar:removed': {
          setServerState(prev => prev ? { ...prev, calendar: undefined } : prev);
          break;
        }
        // GM Studio beats (GM-only, like timeline/sessions): keep serverState.beats
        // live so every surface reads the current plan, not the join snapshot.
        case 'beat:created': {
          const beat = msg.beat;
          setServerState(prev => {
            if (!prev) return prev;
            const beats = prev.beats ?? [];
            return beats.some(b => b.id === beat.id) ? prev : { ...prev, beats: [...beats, beat] };
          });
          break;
        }
        case 'beat:updated': {
          const { beatId, changes } = msg;
          setServerState(prev => prev ? { ...prev, beats: (prev.beats ?? []).map(b => b.id === beatId ? { ...b, ...changes } : b) } : prev);
          break;
        }
        case 'beat:deleted': {
          const { beatId } = msg;
          // The server resets the current pointer in the same operation and
          // echoes beat:current-changed; never keep pointing at a deleted beat.
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).filter(b => b.id !== beatId),
            beatTrail: (prev.beatTrail ?? []).filter(id => id !== beatId),
            ...(prev.currentBeatId === beatId ? { currentBeatId: null } : {}),
          } : prev);
          break;
        }
        // GM Studio 4: the beat being played now — one per campaign, the last
        // accepted command wins, the same echo to every GM (never to players).
        // previousBeatId is the top of the server's trail: where «← Вернуться к» leads. The client keeps only that step.
        case 'beat:current-changed': {
          const { beatId, previousBeatId } = msg;
          setServerState(prev => prev ? { ...prev, currentBeatId: beatId, beatTrail: previousBeatId ? [previousBeatId] : [] } : prev);
          break;
        }
        // P04: the beat's whole list of forks — replaced, never merged.
        case 'beat:outcomes-changed': {
          const { beatId, outcomes } = msg;
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).map(b => b.id === beatId ? { ...b, outcomes: outcomes.map(o => ({ ...o })) } : b),
          } : prev);
          break;
        }
        case 'beat:reordered': {
          const orderById = new Map(msg.beatIds.map((id, i) => [id, i]));
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).map(b => {
              const order = orderById.get(b.id);
              return order === undefined ? b : { ...b, order };
            }),
          } : prev);
          break;
        }
        case 'beat:status-changed': {
          const { beatId, status } = msg;
          setServerState(prev => prev ? { ...prev, beats: (prev.beats ?? []).map(b => b.id === beatId ? { ...b, status } : b) } : prev);
          break;
        }
        // GM Studio 3.1: a beat's explicit materials (idempotent per ref).
        case 'beat:material-added': {
          const { beatId, ref } = msg;
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).map(b => {
              if (b.id !== beatId) return b;
              const materials = b.materials ?? [];
              return materials.some(m => sameMaterial(m, ref)) ? b : { ...b, materials: [...materials, ref] };
            }),
          } : prev);
          break;
        }
        case 'beat:material-removed': {
          const { beatId, ref } = msg;
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).map(b => b.id === beatId ? { ...b, materials: (b.materials ?? []).filter(m => !sameMaterial(m, ref)) } : b),
          } : prev);
          break;
        }
        // GM Studio 6: the beat's whole map pair (set + primary) after a map
        // command — replaced, never merged; the server sends them in order.
        case 'beat:maps-changed': {
          const { beatId, pageIds, pageId } = msg;
          setServerState(prev => prev ? {
            ...prev,
            beats: (prev.beats ?? []).map(b => b.id === beatId ? { ...b, pageIds: [...pageIds], pageId } : b),
          } : prev);
          break;
        }
        // A batch (import, AI tool), a snapshot restore or an AI undo replaced
        // the state on the server; its beat:* echoes were suppressed. The
        // recipient's filtered state carries the beats (a player's: none), so
        // the plan follows the server instead of staying stale until a rejoin.
        // A state without a beats array (partial) leaves the plan as it is.
        case 'state:batch-update': {
          const next = msg.state as Partial<SharedSessionState> | undefined;
          if (next === undefined || !Array.isArray(next.beats)) break;
          const beats = next.beats;
          setServerState(prev => prev ? { ...prev, beats, currentBeatId: next.currentBeatId ?? null, beatTrail: next.beatTrail ?? [] } : prev);
          break;
        }
        // Chat pinned sync (Spec 056-B)
        case 'chat:pinned': {
          const { messageId: pinMsgId, pinned: isPinned } = msg as any;
          setServerState(prev => {
            if (!prev) return prev;
            const current = (prev as any).pinnedMessageIds ?? [];
            if (isPinned) {
              return { ...prev, pinnedMessageIds: current.includes(pinMsgId) ? current : [...current, pinMsgId] };
            }
            return { ...prev, pinnedMessageIds: current.filter((id: string) => id !== pinMsgId) };
          });
          break;
        }
      }

      // Notify external handlers
      for (const handler of handlersRef.current) {
        handler(msg);
      }
    };

    ws.onclose = () => {
      // Guard: only clean up if this ws is still the current one (prevents race with rapid reconnects)
      if (wsRef.current !== ws) return;
      wsRef.current = null;

      // Clear heartbeat timer
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }

      sendReadyRef.current = false;
      setStatus('disconnected');

      if (!intentionalCloseRef.current && pendingJoinRef.current) {
        // Exponential backoff with jitter
        const attempt = reconnectAttemptRef.current++;
        const delay = Math.min(BASE_RECONNECT_DELAY * Math.pow(2, attempt), MAX_RECONNECT_DELAY);
        const jitter = delay * 0.2 * Math.random();
        reconnectTimerRef.current = setTimeout(connect, delay + jitter);
      }
    };

    ws.onerror = () => {
      // onclose will fire after this
    };
  }, [flushPending]);

  const send = useCallback((partial: DistributiveOmit<ClientMessage, 'id' | 'timestamp'>): string | undefined => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN || !sendReadyRef.current) {
      clientLog.warn('ws', `send dropped: ${partial.type}`, { readyState: ws?.readyState, sendReady: sendReadyRef.current });
      return undefined;
    }

    const traceId = crypto.randomUUID().slice(0, 12);
    const msg = {
      ...partial,
      id: makeId(),
      traceId,
      timestamp: new Date().toISOString(),
    };
    ws.send(JSON.stringify(msg));
    return msg.id;
  }, []);

  const createSession = useCallback((name: string, gameSystem?: GameSystemId) => {
    pendingCreateRef.current = name;
    pendingGameSystemRef.current = gameSystem;
    authFailureCountRef.current = 0;
    intentionalCloseRef.current = false;
    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN && !authPendingRef.current) {
      // Already connected and authenticated — send directly
      flushPending(ws);
    } else {
      // Connect — onopen + auth flow will flush pending
      connect();
    }
  }, [connect, flushPending]);

  const joinSession = useCallback((code: string, name: string) => {
    // Preserve participantId only when reconnecting to the SAME session
    const existingPid = pendingJoinRef.current?.code === code ? pendingJoinRef.current.participantId : undefined;

    // Always tear down existing connection/timers when switching sessions
    if (pendingJoinRef.current && pendingJoinRef.current.code !== code) {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      intentionalCloseRef.current = true;
      wsRef.current?.close();
      wsRef.current = null;
      intentionalCloseRef.current = false;
      reconnectAttemptRef.current = 0;
      setSession(null);
      setParticipant(null);
      setParticipants([]);
      setServerState(null);
      setError(null);
    }

    pendingJoinRef.current = { code, name, participantId: existingPid };
    persistSession(pendingJoinRef.current);
    authFailureCountRef.current = 0;
    intentionalCloseRef.current = false;

    const ws = wsRef.current;
    if (ws?.readyState === WebSocket.OPEN && !authPendingRef.current) {
      flushPending(ws);
    } else {
      connect();
    }
  }, [connect, flushPending]);

  const disconnect = useCallback(() => {
    intentionalCloseRef.current = true;
    authFailureCountRef.current = 0;
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
    wsRef.current?.close();
    wsRef.current = null;
    setStatus('disconnected');
    setSession(null);
    setParticipant(null);
    setParticipants([]);
    setServerState(null);
    setTimelineRejections([]);
    setIsRejoining(false);
    pendingJoinRef.current = null;
    pendingCreateRef.current = null;
    pendingGameSystemRef.current = undefined;
    persistSession(null);
    intentionalCloseRef.current = false;
  }, []);

  const onMessage = useCallback((handler: (msg: ServerMessage) => void) => {
    handlersRef.current.add(handler);
    return () => { handlersRef.current.delete(handler); };
  }, []);

  // Reconnect WebSocket when tab becomes visible (iOS Safari kills WS in background)
  // Uses probe approach: send heartbeat, wait for ack, close only if no response
  const probeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const PROBE_TIMEOUT = 5_000; // 5s to receive heartbeat:ack

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || !pendingJoinRef.current) return;

      const ws = wsRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        // Connection is gone — reconnect
        connect();
      } else if (ws.readyState === WebSocket.OPEN) {
        // Connection looks alive — probe it with a heartbeat
        // If server responds with heartbeat:ack within PROBE_TIMEOUT, connection is fine
        // If not, the connection is dead — close and let onclose reconnect
        try {
          ws.send(JSON.stringify({
            id: makeId(),
            type: 'heartbeat',
            timestamp: new Date().toISOString(),
          }));
        } catch {
          // send failed — connection is dead
          ws.close();
          return;
        }

        // Set a timeout — if we don't receive any message within PROBE_TIMEOUT, close
        if (probeTimerRef.current) clearTimeout(probeTimerRef.current);
        const probeSentAt = Date.now();
        probeTimerRef.current = setTimeout(() => {
          probeTimerRef.current = null;
          // Check if we received any message since the probe was sent
          if (lastMessageTimeRef.current < probeSentAt) {
            // No messages received since probe — connection is dead
            wsRef.current?.close();
          }
        }, PROBE_TIMEOUT);
      }
      // CLOSING / CONNECTING — let existing flow handle it
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (probeTimerRef.current) clearTimeout(probeTimerRef.current);
    };
  }, [connect]);

  // Auto-rejoin: if there's a pending session from sessionStorage, connect immediately on mount
  useEffect(() => {
    if (pendingJoinRef.current && !wsRef.current) {
      connect();
    }
  // Run only on mount
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount (must handle React 19 StrictMode double-invoke)
  useEffect(() => {
    // Reset intentionalClose on mount — StrictMode sets it to true during
    // cleanup, then re-runs effects. Without this reset, the reconnect flow
    // stays blocked because onclose sees intentionalCloseRef=true.
    intentionalCloseRef.current = false;
    return () => {
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      intentionalCloseRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, []);

  return {
    status,
    session,
    participant,
    participants,
    serverState,
    initialJournal,
    joinSeq,
    error,
    createSession,
    joinSession,
    send,
    disconnect,
    onMessage,
    isRejoining,
    timelineRejections,
  };
}
